import { FaEdit, FaEye, FaEyeSlash, FaSave, FaTrash } from 'react-icons/fa';
import React, { useState, useEffect } from 'react';
import styles from './styles/CategoryCrud.module.css';
import shared from './styles/adminShared.module.css';
import PrimaryButton from '../PrimaryButton';

interface Category {
    idCategoria: number;
    nombre: string;
    imagen_url?: string;
    visible: boolean;
    orden: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const IMAGE_SIZE_HELP = 'Ideal: imagen cuadrada 1200 x 1200 px, formato JPG/PNG/WEBP y menor a 1 MB.';

const getApiErrorMessage = async (res: Response, fallback: string) => {
    try {
        const data = await res.json();
        return data?.error || data?.message || fallback;
    } catch {
        return fallback;
    }
};

const CategoryCrud: React.FC = () => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [configMessage, setConfigMessage] = useState('');
    const [savingConfig, setSavingConfig] = useState(false);

    // Estado del formulario
    const [formData, setFormData] = useState({ idCategoria: 0, nombre: '', imagen_url: '' });
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_URL}/categorias?include_hidden=1`);
            if (!res.ok) {
                throw new Error(await getApiErrorMessage(res, 'Error al cargar categorias'));
            }
            const data = await res.json();
            if (Array.isArray(data)) {
                // Deduplicate by ID
                const uniqueData = data.filter((item, index, self) =>
                    index === self.findIndex((t) => (
                        t.idCategoria === item.idCategoria
                    ))
                );

                if (uniqueData.length !== data.length) {
                    console.warn(`Duplicate categories detected! Original: ${data.length}, Unique: ${uniqueData.length}`);
                }

                const ordered = uniqueData.sort((a, b) => Number(a.orden) - Number(b.orden));
                setCategories(ordered.map((category, orden) => ({
                    ...category,
                    visible: category.visible !== false && Number(category.visible) !== 0,
                    orden
                })));
            } else {
                // Si devuelve error
                setCategories([]);
                setError('La API de categorias devolvio un formato inesperado.');
            }
        } catch (err) {
            setError('Error al cargar categorías');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const url = isEditing
            ? `${API_URL}/admin/categorias/${formData.idCategoria}`
            : `${API_URL}/admin/categorias`;

        const method = isEditing ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    // 'Authorization': `Bearer ${token}` // Aquí iría el token si lo protegiéramos estrictamente
                },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                fetchCategories();
                resetForm();
            } else {
                setError('Error al guardar la categoría');
            }
        } catch (err) {
            setError('Error de conexión');
        }
    };

    const handleEdit = (cat: Category) => {
        setFormData({
            idCategoria: cat.idCategoria,
            nombre: cat.nombre,
            imagen_url: cat.imagen_url || ''
        });
        setIsEditing(true);
    };

    const handleDelete = async (id: number) => {
        setError('');
        try {
            const impactRes = await fetch(`${API_URL}/admin/categorias/${id}/impacto`);
            if (!impactRes.ok) {
                throw new Error(await getApiErrorMessage(impactRes, 'No se pudo calcular el impacto de la eliminación'));
            }
            const impact = await impactRes.json();
            const confirmed = window.confirm(
                `ADVERTENCIA: vas a eliminar la categoría "${impact.nombre}".\n\n` +
                `• Se eliminarán definitivamente ${impact.subcategorias} subcategoría(s).\n` +
                `• ${impact.productos} producto(s) quedarán sin categoría ni subcategoría.\n` +
                `• Los productos, su stock y su historial NO se eliminarán.\n\n` +
                `¿Querés continuar?`
            );
            if (!confirmed) return;

            const res = await fetch(`${API_URL}/admin/categorias/${id}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                fetchCategories();
            } else {
                setError(await getApiErrorMessage(res, 'Error al eliminar'));
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error de conexión');
        }
    };

    const resetForm = () => {
        setFormData({ idCategoria: 0, nombre: '', imagen_url: '' });
        setIsEditing(false);
    };

    const changeOrder = (id: number, newPosition: number) => {
        setConfigMessage('');
        setCategories(current => {
            const ordered = [...current].sort((a, b) => a.orden - b.orden);
            const currentIndex = ordered.findIndex(category => category.idCategoria === id);
            if (currentIndex < 0) return current;
            const [selected] = ordered.splice(currentIndex, 1);
            ordered.splice(newPosition, 0, selected);
            return ordered.map((category, orden) => ({ ...category, orden }));
        });
    };

    const toggleVisibility = (id: number) => {
        setConfigMessage('');
        setCategories(current => current.map(category =>
            category.idCategoria === id ? { ...category, visible: !category.visible } : category
        ));
    };

    const saveDisplayConfig = async () => {
        setSavingConfig(true);
        setError('');
        setConfigMessage('');
        try {
            const res = await fetch(`${API_URL}/admin/categorias/orden`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: categories.map((category, orden) => ({
                        idCategoria: category.idCategoria,
                        visible: category.visible,
                        orden
                    }))
                })
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'No se pudo guardar'));
            setConfigMessage('Orden y visibilidad guardados correctamente.');
            await fetchCategories();
            window.dispatchEvent(new Event('categories-config-updated'));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'No se pudo guardar');
        } finally {
            setSavingConfig(false);
        }
    };

    return (
        <div className={styles['category-crud-container']}>
            <h3>Gestión de Categorías</h3>

            {error && <div className={styles['error-msg']}>{error}</div>}

            <div className={styles['category-crud-form']}>
                <h4>{isEditing ? 'Editar Categoría' : 'Nueva Categoría'}</h4>
                <form onSubmit={handleSubmit}>
                    <div className={styles['form-group']}>
                        <label>Nombre:</label>
                        <input
                            type="text"
                            name="nombre"
                            value={formData.nombre}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className={styles['form-group']}>
                        <label>URL Imagen (Opcional):</label>
                        <input
                            type="text"
                            name="imagen_url"
                            value={formData.imagen_url}
                            onChange={handleChange}
                            placeholder="https://ejemplo.com/imagen.jpg"
                        />
                        <small style={{ color: '#d6d6d6', fontSize: '0.78rem' }}>{IMAGE_SIZE_HELP}</small>
                    </div>
                    <PrimaryButton type="submit">
                        {isEditing ? 'Actualizar' : 'Crear'}
                    </PrimaryButton>
                    {isEditing && (
                        <button type="button" onClick={resetForm} className={styles['btn-secondary']}>
                            Cancelar
                        </button>
                    )}
                </form>
            </div>

            <div className={styles['crud-list']}>
                <div className={styles['display-config-header']}>
                    <div>
                        <strong>Visibilidad y orden público</strong>
                        <span>Controla cómo aparecen en el navbar y en Productos.</span>
                    </div>
                    <button type="button" onClick={saveDisplayConfig} className={styles['btn-save-config']} disabled={savingConfig}>
                        <FaSave /> {savingConfig ? 'Guardando...' : 'Guardar orden'}
                    </button>
                </div>
                {configMessage && <div className={styles['success-msg']}>{configMessage}</div>}
                <div className={shared.asyncRegion}>
                {loading ? <p className={shared.loadingState}>Cargando...</p> : (
                    <table className={styles['category-crud-table']}>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Nombre</th>
                                <th>Visible</th>
                                <th>Orden</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.map(cat => (
                                <tr key={cat.idCategoria}>
                                    <td>{cat.idCategoria}</td>
                                    <td>{cat.nombre}</td>
                                    <td>
                                        <button type="button" onClick={() => toggleVisibility(cat.idCategoria)} className={styles['btn-visibility']}>
                                            {cat.visible ? <><FaEye /> Visible</> : <><FaEyeSlash /> Oculta</>}
                                        </button>
                                    </td>
                                    <td>
                                        <select
                                            className={styles['order-select']}
                                            value={cat.orden}
                                            onChange={event => changeOrder(cat.idCategoria, Number(event.target.value))}
                                            aria-label={`Orden de ${cat.nombre}`}
                                        >
                                            {categories.map((_, index) => <option key={index} value={index}>{index + 1}</option>)}
                                        </select>
                                    </td>
                                    <td>
                                        <button onClick={() => handleEdit(cat)} className={styles['btn-icon']}><FaEdit /></button>
                                        <button onClick={() => handleDelete(cat.idCategoria)} className={`${styles['btn-icon']} ${styles.delete}`}><FaTrash /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                </div>
            </div>
        </div>
    );
};

export default CategoryCrud;
